import {
  BKJListItem,
  BKJSelect,
  BKJTextArea,
  BKJCheckbox,
  BKJIcon,
  BKJIconTooltip,
  BKJToggleInput,
  BKJLoadingSpinner,
} from '@bookjane2/bookjane-design-library';
import { AutoMargin } from 'components/AutoMargin';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import {
  ShiftModalDataProvider,
  useShiftModalDataContext,
} from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { releaseReasons } from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftView.constant';
import {
  useReleaseShiftViewContext,
  ReleaseShiftViewProvider,
  getIsSkillsCriteriaEditable,
} from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftView.context';
import {
  FormGroup,
  InnerWrapper,
  ReleaseContent,
  ReleaseShiftTitle,
  ReleaseShiftViewWrapper,
  ConfirmationButton,
  AddTimeOffSectionWrapper,
  AddTimeOffTitle,
  AddTimeOffDescription,
  AddTimeOffCheckbox,
  AddTimeOffCheckboxLabel,
  SkillsSectionTitle,
  SkillsTooltipText,
} from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftView.styled';
import { FormLabel } from 'pages/CreateShiftsPage/BulkCreateShiftView/CreateShiftCard/CreateShiftCard.styled';
import { ReleaseShiftViewWarning } from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftViewWarning';
import { useTranslation } from 'hooks/useTranslation';
import { FC, Fragment, useCallback, useMemo } from 'react';
import { OrderStatusEnum } from 'types/graphql';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { useSelector } from 'react-redux';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { isTimeOffReadAccessForFacilitySelector } from 'store/selectors/scheduleSelectors/isTimeOffReadAccessForFacilitySelector';
import { useReleaseShiftTimeOffData } from 'modals/ShiftModal/src/views/ReleaseShiftView/useReleaseShiftTimeOffData';
import { Flex } from 'components/Flex';
import { ShiftCustomFieldInput } from 'modals/ShiftModal/src/components/ShiftCustomFieldInput/ShiftCustomFieldInput';
import { MobileLabel } from 'pages/CreateShiftsPage/BulkCreateShiftView/CreateShiftModal/src/views/DetailsView/DetailsView.styled';
import { shiftModalEditViewFormItemVerticalMargin } from 'modals/ShiftModal/src/views';
import { SectionDivider } from 'modals/ShiftModal/ShiftModal.styled';
import {
  AllDaySection,
  TimeOffDateTimeWrapper,
  TimeOffRow,
  TimeOffColumn,
  NotesSection,
} from 'modals/TimeOffModal/TimeOffModalView.styled';
import { ShiftDateCalendarInput } from 'components/ShiftDateCalendarInput';
import { ShiftTimeSelectInput } from 'components/ShiftTimeSelectInput';
import { titleOptions, calendarDropdownProps } from 'modals/TimeOffModal/TimeOffModal.constants';
import {
  TimeOffPayCodeField,
  useTimeOffPayCodes,
} from 'modals/TimeOffModal/components/TimeOffPayCodeField';
import { css } from 'styled-components';
import { withMemo } from 'utils/withMemo';
import { IBKJSelectRenderOptionsProps } from '@bookjane2/bookjane-design-library/lib/components/BKJSelect/BKJSelect.types';
import { useIsAgencyShift } from 'hooks/useAgencyHooks';
import { useURLParameters } from 'hooks';
import { ShiftModalErrorView } from 'modals/ShiftModal/src/views/ShiftModalErrorView';
import { SkillsSelectInput, useCommunitySkills } from 'components/SkillsSelectInput';
import { useCanManageSkillsSelector } from 'store/selectors/skillsSelectors/canManageSkillsSelector';

const ReleaseShiftViewInner: FC = () => {
  const { t, i18n } = useTranslation();

  const {
    values,
    onChange,
    isDisabled: isReleaseFormDisabled,
    submitRelease,
    isSubmitting,
    fieldErrors,
    timeOffCustomFields,
    isSkillsCriteriaEditable,
    isAllowOvertimeEditable,
    communitySkillOptions,
  } = useReleaseShiftViewContext();
  const { data } = useShiftModalDataContext();
  const order = data?.order;

  const status = order?.status;
  const mainJane = order?.mainJane;

  const isCustomFieldsEnabled =
    useSelector(FEATURE_SELECTORS_MAP['FEATURE_TYPE_CUSTOM_FIELDS']) && !order?.isExternal;
  const canCreateTimeOffForMainJane = useSelector(
    isTimeOffReadAccessForFacilitySelector(Number(mainJane?.primaryCommunity?.id)),
  );

  const isAgencyShift = useIsAgencyShift({
    mainJane,
    sentToAgencies: order.sentToAgencies,
    orderCommunityOrgId: order.community?.organization?.id,
  });

  const isAgencyTeamMember = isAgencyShift && !!mainJane;

  const shouldShowTimeOffSection =
    values.reason_category === 'paid_time_off' || values.reason_category === 'unpaid_time_off';

  const timeOffSectionVisible =
    shouldShowTimeOffSection &&
    !!mainJane?.id &&
    !isAgencyTeamMember &&
    canCreateTimeOffForMainJane;

  const { timeOffPayload } = useReleaseShiftTimeOffData({
    mainJaneId: mainJane?.id,
    mainJanePrimaryCommunityId: mainJane?.primaryCommunity?.id,
    shouldValidate: timeOffSectionVisible,
  });

  const dontAddTimeOff = values.dontAddTimeOff;
  const { startDate, startTime, endDate, endTime, allDay, title, pay_code_id, notes = '' } = values;

  const { payCodeOptions, handleTitleChange, isPayCodeBlockingSubmit } = useTimeOffPayCodes({
    title,
    payCodeId: pay_code_id,
    onChange,
    isEnabled: timeOffSectionVisible,
  });

  const translatedTimeOffOptions = useMemo(
    () =>
      titleOptions.map((option) => ({
        ...option,
        label: t(option.label),
      })),
    [t],
  );

  const RenderTimeOffOptions: FC<IBKJSelectRenderOptionsProps> = withMemo(
    function _RenderTimeOffOptions(props) {
      return (
        <Fragment>
          {translatedTimeOffOptions.map((option) => {
            const { type, ...restProps } = props;
            return <BKJListItem {...restProps} {...option} />;
          })}
        </Fragment>
      );
    },
  );

  const isShiftAssignedOrAcceptedOrSwapped = status?.value
    ? [OrderStatusEnum.assigned, OrderStatusEnum.accepted, OrderStatusEnum.swapped].includes(
        status.value,
      )
    : false;

  const transformedReleaseReasons = releaseReasons.filter((reason) => {
    if (isShiftAssignedOrAcceptedOrSwapped)
      return reason.value !== 'communicated_absence' && reason.value !== 'no_show';
    return releaseReasons;
  });

  const releaseButtonDisabled =
    isReleaseFormDisabled ||
    isSubmitting ||
    (timeOffPayload.shouldInclude &&
      (timeOffPayload.hasErrors || !timeOffPayload.payload || isPayCodeBlockingSubmit));

  const handleReleaseClick = useCallback(() => {
    submitRelease(timeOffPayload.shouldInclude ? timeOffPayload.payload : undefined);
  }, [submitRelease, timeOffPayload]);

  return (
    <Fragment>
      <ReleaseShiftViewWrapper flexDirection="column" flex="1">
        <ReleaseShiftTitle>{t('shift_modal:RELEASE_TEAM_MEMBER_CONFIRMATION')}</ReleaseShiftTitle>
        <InnerWrapper>
          <ReleaseShiftViewWarning />
          <FormGroup>
            <FormLabel>{t('shift_modal:REASON_FOR_RELEASE')}:*</FormLabel>
            <BKJSelect
              options={transformedReleaseReasons}
              type="Single"
              width="100%"
              variant="GreyOutlined"
              placeholder={t('shift_modal:SELECT_A_REASON')}
              value={[values.reason_category]}
              name="reason_category"
              required
              onChange={onChange}
              renderOptions={({ options, selected, onChange }) => {
                return (
                  <Fragment>
                    {transformedReleaseReasons.map(({ key, label, value }) => {
                      const translatedLabel = getTranslatedResponse(label, 'status', i18n, t);
                      return (
                        <BKJListItem
                          name="reason_category"
                          selected={selected}
                          key={key}
                          label={translatedLabel}
                          value={value}
                          onChange={onChange}
                        />
                      );
                    })}
                  </Fragment>
                );
              }}
            />
          </FormGroup>
          {values.reason_category === 'other' && (
            <FormGroup>
              <FormLabel>{t('shift_modal:PLEASE_SPECIFY_REASON')}:*</FormLabel>
              <BKJTextArea
                name="reason_message"
                placeholder={t('shift_modal:REASON_VISIBLE_TO_FACILITY_AND_TEAM_MEMBER')}
                value={values.reason_message}
                onChange={onChange}
                maxLength={500}
              />
            </FormGroup>
          )}

          <ReleaseContent>
            {isAgencyTeamMember
              ? t('shift_modal:AGENCY_CUSTOMER_AND_TEAM_MEMBER_WILL_BE_NOTIFIED')
              : t('shift_modal:CUSTOMER_AND_TEAM_MEMBER_WILL_BE_NOTIFIED')}
          </ReleaseContent>
          {isSkillsCriteriaEditable && communitySkillOptions.length > 0 && (
            <Fragment>
              <Flex alignItems="center" gap="8px" padding="24px 0 16px 0">
                <SkillsSectionTitle>
                  {t('shift_modal:UPDATE_SHIFT_SKILLS_CRITERIA')}
                </SkillsSectionTitle>
                <BKJIconTooltip placement="bottom" iconName="Info">
                  <SkillsTooltipText>
                    {t('shift_modal:UPDATE_SHIFT_SKILLS_CRITERIA_TOOLTIP')}
                  </SkillsTooltipText>
                </BKJIconTooltip>
              </Flex>
              <FormGroup>
                <SkillsSelectInput
                  name="required_skills"
                  value={values.required_skills}
                  onChange={onChange}
                  options={communitySkillOptions}
                  label={t('create_shifts:SKILLS')}
                  placeholder={t('create_shifts:SELECT_SKILLS')}
                  max={communitySkillOptions.length}
                />
              </FormGroup>
            </Fragment>
          )}
          {timeOffSectionVisible && (
            <AddTimeOffSectionWrapper>
              <AddTimeOffTitle>{t('schedule:ADD_TIME_OFF')}</AddTimeOffTitle>
              <AddTimeOffDescription>
                {t('schedule:ADDING_TIME_OFF_WILL_BLOCK_THIS_TEAM_MEMBER')}
              </AddTimeOffDescription>
              <AddTimeOffCheckbox>
                <BKJCheckbox
                  value={dontAddTimeOff}
                  name="dontAddTimeOff"
                  variant="GreenSolid"
                  onChange={onChange}
                />
                <AddTimeOffCheckboxLabel>{t('schedule:DONT_ADD_TIME_OFF')}</AddTimeOffCheckboxLabel>
              </AddTimeOffCheckbox>

              {!dontAddTimeOff && (
                <Fragment>
                  <Flex width="100%" flexDirection="column">
                    <FormLabel>{t('schedule:TIME_OFF_TYPE')}*</FormLabel>
                    <BKJSelect
                      options={translatedTimeOffOptions}
                      type="Single"
                      variant="GreyOutlined"
                      dropdownPlacement="bottom-start"
                      name="title"
                      onChange={handleTitleChange}
                      value={title}
                      renderOptions={RenderTimeOffOptions}
                      required
                    />
                  </Flex>

                  <TimeOffPayCodeField
                    value={pay_code_id}
                    onChange={onChange}
                    options={payCodeOptions}
                  />

                  <AllDaySection>
                    <BKJCheckbox
                      value={allDay}
                      name="allDay"
                      variant="GreenSolid"
                      onChange={onChange}
                      css={css`
                        margin-right: 12px;
                      `}
                    />
                    {t('schedule:ALL_DAY')}
                  </AllDaySection>

                  <Flex flexDirection="column" width="100%" gap="8px">
                    <TimeOffDateTimeWrapper>
                      <TimeOffRow>
                        <TimeOffColumn>
                          <FormLabel>{t('schedule:STARTS')}</FormLabel>
                          <ShiftDateCalendarInput
                            name="startDate"
                            iconComponent={BKJIcon}
                            iconProps={{
                              iconName: 'ShiftsDate',
                            }}
                            error={fieldErrors?.startDate}
                            value={startDate}
                            onChange={onChange}
                            dropdownProps={calendarDropdownProps}
                          />
                        </TimeOffColumn>
                        <TimeOffColumn>
                          {!allDay && (
                            <ShiftTimeSelectInput
                              label={t('schedule:START_TIME')}
                              value={startTime}
                              placeholder={t('schedule:START_TIME')}
                              onChange={onChange}
                              error={fieldErrors?.startTime}
                              name="startTime"
                            />
                          )}
                        </TimeOffColumn>
                      </TimeOffRow>
                      <TimeOffRow>
                        <TimeOffColumn>
                          <FormLabel>{t('schedule:ENDS')}</FormLabel>
                          <ShiftDateCalendarInput
                            iconComponent={BKJIcon}
                            iconProps={{
                              iconName: 'ShiftsDate',
                            }}
                            error={fieldErrors?.endDate}
                            name="endDate"
                            value={endDate}
                            onChange={onChange}
                            dropdownProps={calendarDropdownProps}
                          />
                        </TimeOffColumn>
                        <TimeOffColumn>
                          {!allDay && (
                            <ShiftTimeSelectInput
                              label={t('schedule:END_TIME')}
                              value={endTime}
                              placeholder={t('schedule:END_TIME')}
                              onChange={onChange}
                              name="endTime"
                              error={fieldErrors?.endTime}
                            />
                          )}
                        </TimeOffColumn>
                      </TimeOffRow>
                    </TimeOffDateTimeWrapper>
                  </Flex>

                  <NotesSection>
                    <BKJTextArea
                      label={t('schedule:NOTES')}
                      name="notes"
                      placeholder={t(
                        'schedule:NOTES_WILL_ONLY_BE_SHARED_WITH_FACILITY_ADMINISTRATORS',
                      )}
                      onChange={onChange}
                      maxLength={1000}
                      value={notes}
                    />
                  </NotesSection>

                  {isCustomFieldsEnabled && timeOffCustomFields.length > 0 && (
                    <Fragment>
                      <SectionDivider
                        variant="Thin"
                        margin={`${shiftModalEditViewFormItemVerticalMargin}px 0`}
                      />
                      {timeOffCustomFields.map((customField) => {
                        return (
                          <Flex
                            key={customField.id}
                            flexDirection="column"
                            margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
                          >
                            <FormLabel>
                              {customField.name}
                              {customField.mobile && (
                                <MobileLabel>
                                  &nbsp;({t('schedule:SHOWN_INTERNALLY_ON_MOBILE')})
                                </MobileLabel>
                              )}
                            </FormLabel>
                            <ShiftCustomFieldInput
                              key={customField.id}
                              customField={customField}
                              value={(values?.[customField?.name] as string) ?? ''}
                              onChange={onChange}
                            />
                          </Flex>
                        );
                      })}
                    </Fragment>
                  )}
                </Fragment>
              )}
            </AddTimeOffSectionWrapper>
          )}
          {isAllowOvertimeEditable && (
            <Flex alignItems="center" justifyContent="space-between" gap="16px" margin="24px 0 0 0">
              <ReleaseContent>{t('shift_modal:ALLOW_OVERTIME')}</ReleaseContent>
              <BKJToggleInput
                variant="Switch"
                name="allow_overtime"
                onChange={onChange}
                checked={values.allow_overtime}
              />
            </Flex>
          )}
        </InnerWrapper>
      </ReleaseShiftViewWrapper>
      <BKJSideModalFooter>
        <AutoMargin />
        <ConfirmationButton
          width="180px"
          variant="PurpleSolid"
          disabled={releaseButtonDisabled}
          isLoading={isSubmitting}
          onClick={handleReleaseClick}
        >
          {t('shift_modal:RELEASE')}
        </ConfirmationButton>
      </BKJSideModalFooter>
    </Fragment>
  );
};

const ReleaseShiftViewLoader: FC<{ isConflictedRelease?: boolean }> = ({ isConflictedRelease }) => {
  const { data, status } = useShiftModalDataContext();
  const order = data?.order;
  const canManageSkills = useCanManageSkillsSelector();
  const isSkillsCriteriaEditable = getIsSkillsCriteriaEditable(canManageSkills, order);

  const { options: communitySkillOptions, isLoading } = useCommunitySkills(
    isSkillsCriteriaEditable ? Number(order?.community?.displayId) : undefined,
  );

  const isAwaitingSkillOptions =
    isSkillsCriteriaEditable &&
    isLoading &&
    !communitySkillOptions.length &&
    !order?.ordersSkills?.length;

  if (!order || isAwaitingSkillOptions) {
    if (status === 'ERROR') return <ShiftModalErrorView />;
    return <BKJLoadingSpinner variant="Default" />;
  }

  return (
    <ReleaseShiftViewProvider
      isConflictedRelease={isConflictedRelease}
      communitySkillOptions={communitySkillOptions}
    >
      <ReleaseShiftViewInner />
    </ReleaseShiftViewProvider>
  );
};

const ConflictedShiftGate: FC<{ conflictedShiftId: string }> = ({ conflictedShiftId }) => {
  const { data, status } = useShiftModalDataContext();

  if (data?.order?.relayId !== conflictedShiftId) {
    if (status === 'ERROR') return <ShiftModalErrorView />;
    return <BKJLoadingSpinner variant="Default" />;
  }

  return <ReleaseShiftViewLoader isConflictedRelease />;
};

export const ReleaseShiftView: FC = () => {
  const [{ conflictedShiftId, conflictedShiftCommunityId }] =
    useURLParameters<{
      conflictedShiftId?: string;
      conflictedShiftCommunityId?: string;
    }>();

  if (conflictedShiftId) {
    return (
      <ShiftModalDataProvider
        locationId={Number(conflictedShiftCommunityId)}
        shiftId={conflictedShiftId}
      >
        <ConflictedShiftGate conflictedShiftId={conflictedShiftId} />
      </ShiftModalDataProvider>
    );
  }

  return <ReleaseShiftViewLoader />;
};
